import { createFileRoute, Link } from "@tanstack/react-router";
import { Monitor, Moon, Sun, Sparkles, RotateCcw, Check } from "lucide-react";
import {
  useDisplaySettings,
  type TextSize,
  type ThemeMode,
} from "@/components/DisplaySettings";

export const Route = createFileRoute("/settings/accessibility")({
  head: () => ({
    meta: [
      { title: "Accessibility & Display — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Personalise your experience: dark mode, text size, high contrast, increased spacing, and larger touch targets. Settings save automatically.",
      },
      {
        property: "og:title",
        content: "Accessibility & Display — Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Personalise theme, text size, contrast, spacing, and touch targets for a more comfortable experience.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccessibilitySettingsPage,
});

const TEXT_SIZE_OPTIONS: { value: TextSize; label: string; preview: string }[] =
  [
    { value: "sm", label: "Small", preview: "Aa" },
    { value: "md", label: "Medium", preview: "Aa" },
    { value: "lg", label: "Large", preview: "Aa" },
    { value: "xl", label: "Extra Large", preview: "Aa" },
  ];

const THEME_OPTIONS: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

function AccessibilitySettingsPage() {
  const { prefs, setPrefs, reset } = useDisplaySettings();

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-24 pt-2">
      <header className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          Profile · Settings
        </p>
        <h1 className="mt-2 font-display text-2xl text-foreground sm:text-3xl">
          Accessibility &amp; Display
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Adjust how Recovery Trauma™ looks and feels. Your preferences save
          automatically on this device.
        </p>
      </header>

      {/* Theme */}
      <Section title="Theme" subtitle="Choose how the app looks.">
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => {
            const active = prefs.theme === value;
            return (
              <button
                key={value}
                onClick={() => setPrefs({ theme: value })}
                className={`flex flex-col items-center gap-2 rounded-2xl border p-4 text-sm transition ${
                  active
                    ? "border-[var(--gold-deep)] bg-accent/40 text-foreground"
                    : "border-border bg-card text-foreground/70 hover:bg-accent/50"
                }`}
                aria-pressed={active}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{label}</span>
                {active && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[var(--gold-deep)]">
                    <Check className="h-3 w-3" /> Active
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Section>

      {/* Text size */}
      <Section
        title="Text size"
        subtitle="Make the text more comfortable to read."
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          {TEXT_SIZE_OPTIONS.map(({ value, label, preview }) => {
            const active = prefs.textSize === value;
            const sizeClass =
              value === "sm"
                ? "text-base"
                : value === "md"
                  ? "text-lg"
                  : value === "lg"
                    ? "text-xl"
                    : "text-2xl";
            return (
              <button
                key={value}
                onClick={() => setPrefs({ textSize: value })}
                className={`flex flex-col items-center gap-1 rounded-2xl border p-4 transition ${
                  active
                    ? "border-[var(--gold-deep)] bg-accent/40"
                    : "border-border bg-card hover:bg-accent/50"
                }`}
                aria-pressed={active}
              >
                <span
                  className={`${sizeClass} font-display font-semibold text-foreground`}
                >
                  {preview}
                </span>
                <span className="text-xs text-foreground/70">{label}</span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Accessibility toggles */}
      <Section
        title="Accessibility"
        subtitle="Improve readability and touch comfort."
      >
        <div className="space-y-2">
          <ToggleRow
            label="High contrast"
            description="Stronger contrast for text, borders, and focus rings."
            checked={prefs.highContrast}
            onChange={(v) => setPrefs({ highContrast: v })}
          />
          <ToggleRow
            label="Increased spacing"
            description="More space between lines, paragraphs, and sections."
            checked={prefs.spacious}
            onChange={(v) => setPrefs({ spacious: v })}
          />
          <ToggleRow
            label="Larger touch targets"
            description="Buttons and links grow to at least 44px for easier tapping."
            checked={prefs.largeTargets}
            onChange={(v) => setPrefs({ largeTargets: v })}
          />
        </div>
      </Section>

      {/* Live preview */}
      <Section
        title="Preview"
        subtitle="A quick glimpse of your current settings."
      >
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            Sample
          </p>
          <h3 className="mt-2 font-display text-xl text-foreground">
            You're doing enough today.
          </h3>
          <p className="mt-2 text-sm text-foreground/80">
            Healing is rarely linear. Notice one small kindness you can offer
            yourself right now — a slow breath, a soft shoulder, a glass of
            water.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-1.5 text-sm font-medium text-background">
              <Sparkles className="h-3.5 w-3.5" /> Primary action
            </button>
            <button className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-accent">
              Secondary
            </button>
          </div>
        </div>
      </Section>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={reset}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-foreground/80 hover:bg-accent"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
        </button>
        <Link
          to="/profile"
          className="text-xs font-medium text-[var(--gold-deep)] underline-offset-2 hover:underline"
        >
          Back to Profile
        </Link>
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6 rounded-3xl border border-border bg-card/60 p-5 sm:p-6">
      <h2 className="font-display text-lg text-foreground">{title}</h2>
      {subtitle && (
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      )}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-border bg-background/60 p-4 hover:bg-accent/30">
      <span className="flex-1">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {description}
        </span>
      </span>
      <span
        className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-[var(--gold-deep)]" : "bg-border"
        }`}
      >
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </label>
  );
}
